import React from 'react';
import { Input, DatePicker, Button } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

interface QueryParamsProps {
  employeeFrom: string;
  employeeTo: string;
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
  departmentFrom: string;
  departmentTo: string;
  loading: boolean;
  onEmployeeFromChange: (v: string) => void;
  onEmployeeToChange: (v: string) => void;
  onDateFromChange: (v: Dayjs | null) => void;
  onDateToChange: (v: Dayjs | null) => void;
  onDepartmentFromChange: (v: string) => void;
  onDepartmentToChange: (v: string) => void;
  onQuery: () => void;
  onReset: () => void;
}

const QueryParams: React.FC<QueryParamsProps> = ({
  employeeFrom, employeeTo,
  dateFrom, dateTo,
  departmentFrom, departmentTo,
  loading,
  onEmployeeFromChange, onEmployeeToChange,
  onDateFromChange, onDateToChange,
  onDepartmentFromChange, onDepartmentToChange,
  onQuery, onReset,
}) => {
  /** Auto-fill "to" when "from" loses focus (BR-D01). */
  const handleEmployeeFromBlur = () => {
    if (employeeFrom.trim() && !employeeTo) {
      const padded = (employeeFrom.trim() + 'zzzz').substring(0, 4);
      onEmployeeToChange(padded);
    }
  };

  /** Auto-fill department "to" when "from" loses focus (BR-D02). */
  const handleDepartmentFromBlur = () => {
    if (departmentFrom.trim() && !departmentTo) {
      const padded = (departmentFrom.trim() + 'zzzz').substring(0, 4);
      onDepartmentToChange(padded);
    }
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
      {/* Row 1: Employee range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium w-20">人員代號</span>
          <Input
            value={employeeFrom}
            onChange={(e) => onEmployeeFromChange(e.target.value)}
            onBlur={handleEmployeeFromBlur}
            placeholder="從"
            style={{ width: 120 }}
            maxLength={8}
          />
          <span className="text-sm">~</span>
          <Input
            value={employeeTo}
            onChange={(e) => onEmployeeToChange(e.target.value)}
            placeholder="至"
            style={{ width: 120 }}
            maxLength={8}
          />
        </div>
      </div>

      {/* Row 2: Department range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium w-20">部門代號</span>
          <Input
            value={departmentFrom}
            onChange={(e) => onDepartmentFromChange(e.target.value)}
            onBlur={handleDepartmentFromBlur}
            placeholder="從"
            style={{ width: 120 }}
            maxLength={4}
          />
          <span className="text-sm">~</span>
          <Input
            value={departmentTo}
            onChange={(e) => onDepartmentToChange(e.target.value)}
            placeholder="至"
            style={{ width: 120 }}
            maxLength={4}
          />
        </div>
      </div>

      {/* Row 3: Date range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium w-20">出勤日期</span>
          <DatePicker
            value={dateFrom}
            onChange={onDateFromChange}
            placeholder="起始日期"
            style={{ width: 140 }}
          />
          <span className="text-sm">~</span>
          <DatePicker
            value={dateTo}
            onChange={onDateToChange}
            placeholder="結束日期"
            style={{ width: 140 }}
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="flex gap-2">
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={onQuery}
          loading={loading}
        >
          查詢
        </Button>
        <Button icon={<ReloadOutlined />} onClick={onReset} disabled={loading}>
          重設
        </Button>
      </div>
    </div>
  );
};

export default React.memo(QueryParams);
